import React, { useEffect, useRef, useState } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';

const MapComponent = ({ center, zoom, cities, onCityClick }) => {
  const ref = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);

  useEffect(() => {
    if (ref.current && !map) {
      const newMap = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        styles: [
          {
            featureType: 'all',
            elementType: 'geometry.fill',
            stylers: [{ weight: '2.00' }]
          },
          {
            featureType: 'all',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#9c9c9c' }]
          },
          {
            featureType: 'all',
            elementType: 'labels.text',
            stylers: [{ visibility: 'on' }]
          },
          {
            featureType: 'landscape',
            elementType: 'all',
            stylers: [{ color: '#f2f2f2' }]
          },
          {
            featureType: 'landscape.man_made',
            elementType: 'geometry.fill',
            stylers: [{ color: '#ffffff' }]
          },
          {
            featureType: 'landscape.natural.landcover',
            elementType: 'geometry.fill',
            stylers: [{ color: '#ffffff' }]
          },
          {
            featureType: 'landscape.natural.terrain',
            elementType: 'geometry.fill',
            stylers: [{ color: '#ffffff' }]
          },
          {
            featureType: 'poi',
            elementType: 'all',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'road',
            elementType: 'all',
            stylers: [{ color: '#ffffff' }]
          },
          {
            featureType: 'road',
            elementType: 'labels.icon',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'transit',
            elementType: 'all',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'water',
            elementType: 'all',
            stylers: [{ color: '#46bcec' }, { visibility: 'on' }]
          }
        ]
      });
      setMap(newMap);
    }
  }, [ref, map, center, zoom]);

  useEffect(() => {
    if (map) {
      map.setZoom(zoom);
    }
  }, [map, zoom]);

  useEffect(() => {
    if (map && cities) {
      // Clear existing markers
      markers.forEach(marker => marker.setMap(null));
      
      const newMarkers = cities.map(city => {
        const marker = new window.google.maps.Marker({
          position: { lat: city.lat, lng: city.lng },
          map,
          title: city.name,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="12" fill="#196bbd" stroke="#ffffff" stroke-width="2"/>
                <circle cx="16" cy="16" r="6" fill="#ffffff"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(32, 32),
            anchor: new window.google.maps.Point(16, 16)
          }
        });

        // Add click event listener
        marker.addListener('click', () => {
          if (onCityClick) {
            onCityClick(city.name);
          }
        });

        return marker;
      });

      setMarkers(newMarkers);
    }
  }, [map, cities, onCityClick]);

  return <div ref={ref} style={{ width: '100%', height: '100%', borderRadius: '12px' }} />;
};

const render = (status) => {
  switch (status) {
    case Status.LOADING:
      return (
        <div className="map-loading">
          <div className="spinner"></div>
          <p>Loading map...</p>
        </div>
      );
    case Status.FAILURE:
      return (
        <div className="map-error">
          <p>Error loading map. Please try again.</p>
        </div>
      );
    default:
      return null;
  }
};

const GoogleMap = ({ cities, onCityClick }) => {
  const center = { lat: 20.5937, lng: 78.9629 }; // Center of India
  
  // Adjust zoom level based on screen size
  const getZoomLevel = () => {
    if (window.innerWidth <= 480) {
      return 4; // More zoomed out for small mobile
    } else if (window.innerWidth <= 768) {
      return 4.5; // Slightly more zoomed out for mobile
    } else {
      return 5; // Original zoom for desktop
    }
  };

  const [zoom, setZoom] = useState(getZoomLevel());

  useEffect(() => {
    const handleResize = () => {
      setZoom(getZoomLevel());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Wrapper apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} render={render}>
      <MapComponent 
        center={center} 
        zoom={zoom} 
        cities={cities} 
        onCityClick={onCityClick}
      />
    </Wrapper>
  );
};

export default GoogleMap;
